import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {Form, Typography} from 'antd'
import {useInjection} from 'dna-react-ioc'
import {observer} from 'mobx-react'
import {useTimer} from 'dna-common'

import {ROUTES} from '@/router/routes'
import {withTitle} from '@/router/utils'
import {LoadingState} from '@/types'
import {BackButton, Button, Card, Input} from '@/components'
import {TConfirmEmailStore} from '@/pages/ConfirmEmail'
import {CONFIRM_EMAIL_BUTTON, RESEND_OTP_CODE} from '@/constants/playwright-ids'
import styles from './styles.scss'

export const ConfirmEmailStoreSymbol = Symbol('ConfirmEmailStore')

const ConfirmEmail = observer(() => {
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const store = useInjection<TConfirmEmailStore>(ConfirmEmailStoreSymbol)
    const {start, count, isRunning} = useTimer(60)

    const onFinish = (values) => {
        store.confirmEmail(values)
    }

    const onResendCodeClick = () => {
        start()
        store.onResendClick()
    }

    const formId = 'reset-password'

    return (
        <>
            <div className={styles.resetPassContainer}>
                <BackButton
                    to={ROUTES.resetPassword}
                    className={styles.backToResetPassword}
                    title={t('ConfirmEmail.back')}
                />
                <Card title={t('ConfirmEmail.title')}>
                    <div>
                        <Trans
                            i18nKey='ConfirmEmail.text'
                            values={{ email: store.email }}
                            components={{ strong: <Typography.Text strong />}}
                        />
                    </div>
                    <br />
                    <Form
                        form={form}
                        id={formId}
                        layout={'vertical'}
                        requiredMark={false}
                        onFinish={onFinish}
                    >
                        <Form.Item
                            label={t('ConfirmEmail.otp')}
                            name={'key'}
                            rules={[{required: true, message: t('ConfirmEmail.otpRequired')}]}
                        >
                            <Input
                                maxLength={6}
                                placeholder={t('ConfirmEmail.otp')}
                                data-test-id={'code'}
                            />
                        </Form.Item>
                        <Form.Item data-test-id={RESEND_OTP_CODE}>
                            {isRunning
                                ? (
                                    <Trans
                                        i18nKey='ConfirmEmail.waitingToResendVerificationCode'
                                        values={{ count }}
                                        components={{ a: <a className="disabled" />, span: <span className="count" /> }}
                                    />
                                ) : (
                                    <Trans
                                        i18nKey='ConfirmEmail.confirmEmailDescription'
                                        components={{ a: <a onClick={onResendCodeClick} /> }}
                                    />
                                )}
                        </Form.Item>
                        <Form.Item key={'submit'}>
                            <Button
                                form={formId}
                                type={'primary'}
                                htmlType={'submit'}
                                className={styles.submit}
                                loading={store.confirmEmailLoadingState === LoadingState.LOADING}
                                data-test-id={CONFIRM_EMAIL_BUTTON}
                            >
                                {t('ConfirmEmail.submit')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </>
    )
})

export default withTitle(ConfirmEmail)
